import {
  CompanyGeolocationSiteValues,
  GeolocationSiteSource,
  GeolocationSupplyChainPosition,
} from '@upright/common/model/input/companies/geolocation';
import { SiteLocationInput } from '@upright/common/model/input/companies/geolocation/api-types';
import {
  NO_GEOCODE_VERDICT,
  SiteGeocodeVerdict,
} from '@upright/common/model/input/companies/geolocation/coordinate-check';

/**
 * Editable table-row shape for the site-locations input page.
 *
 * `key` and `_uiId` satisfy the generic `useEditingMode<T extends { key; _uiId? }>` contract
 * (row identity during editing). They are UI-only and stripped before building the save
 * payload. The remaining fields mirror the editable subset of `CompanyGeolocationSite` /
 * `SiteLocationInput`; `latitude`/`longitude` are nullable here and optional
 * on save (PLT-5329 #8) — a site can be stored without coordinates and either geocoded from
 * its address or filled in later.
 */
export interface SiteRow extends SiteGeocodeVerdict {
  key: string;
  _uiId?: string;
  locationType: string;
  name: string | null;
  description: string | null;
  streetAddress: string | null;
  addressLocality: string | null;
  addressRegion: string | null;
  postalCode: string | null;
  addressCountry: string | null;
  supplyChainPosition: GeolocationSupplyChainPosition;
  latitude: number | null;
  longitude: number | null;
  weight: number | null;
  evidenceUrl: string | null;
  /**
   * How the persisted row was authored, carried through untouched so a save keeps it. A save
   * replaces the whole draft, so a row that dropped this would come back as `manual` — and the
   * identified sites the new-company wizard reviews would all lose their provenance on the first
   * edit. Absent on a row added here, which the server then records as `manual`.
   */
  source?: GeolocationSiteSource;
}

/**
 * Whether a row carries a usable coordinate pair.
 *
 * Load-bearing for weights, not just for geocoding: compute resolves site weights
 * over the coordinate-bearing sites alone (`loadPublishedClimateSitesByVariantId`
 * filters on latitude/longitude), so anything that shows or validates an
 * effective weight has to narrow to the same set or it will describe a share the
 * engine never uses.
 */
export function rowHasCoordinates(row: SiteRow): boolean {
  return (
    row.latitude != null && Number.isFinite(row.latitude) && row.longitude != null && Number.isFinite(row.longitude)
  );
}

/** Map a persisted site into an editable table row. */
export function siteToRow(site: CompanyGeolocationSiteValues): SiteRow {
  return {
    key: String(site.id),
    locationType: site.locationType,
    name: site.name,
    description: site.description,
    streetAddress: site.streetAddress,
    addressLocality: site.addressLocality,
    addressRegion: site.addressRegion,
    postalCode: site.postalCode,
    addressCountry: site.addressCountry,
    supplyChainPosition: site.supplyChainPosition,
    latitude: site.latitude,
    longitude: site.longitude,
    // `??` because an approval recorded before PLT-7875 replays its sites without these.
    geocodePrecision: site.geocodePrecision ?? null,
    geocodePartialMatch: site.geocodePartialMatch ?? null,
    weight: site.weight,
    evidenceUrl: site.evidenceUrl,
    source: site.source,
  };
}

/**
 * Map an edited row into the save payload shape. Drops the UI-only `key`/`_uiId`. Coordinates
 * pass through as-is — they are optional (PLT-5329 #8), so a row without them is saved with
 * null latitude/longitude.
 */
export function rowToInput(row: SiteRow): SiteLocationInput {
  return {
    locationType: row.locationType.trim(),
    name: row.name,
    description: row.description,
    streetAddress: row.streetAddress,
    addressLocality: row.addressLocality,
    addressRegion: row.addressRegion,
    postalCode: row.postalCode,
    addressCountry: row.addressCountry,
    supplyChainPosition: row.supplyChainPosition,
    evidenceUrl: row.evidenceUrl,
    latitude: row.latitude,
    longitude: row.longitude,
    geocodePrecision: row.geocodePrecision,
    geocodePartialMatch: row.geocodePartialMatch,
    weight: row.weight,
    source: row.source,
  };
}

/** A blank row used by the "Add site" action. Defaults the required enum to a sensible value. */
export function emptySiteRow(): SiteRow {
  return {
    key: '',
    locationType: '',
    name: null,
    description: null,
    streetAddress: null,
    addressLocality: null,
    addressRegion: null,
    postalCode: null,
    addressCountry: null,
    supplyChainPosition: 'Internal',
    latitude: null,
    longitude: null,
    ...NO_GEOCODE_VERDICT,
    weight: null,
    evidenceUrl: null,
  };
}
